import {
  Coins,
  Image as ImageIcon,
  Layers,
  LogOut,
  Plug,
  SlidersHorizontal,
} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {logout} from 'src/LogoutButton';
import {useIsMobile} from 'src/useIsMobile';

type SettingsPage = {
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
};

export const SETTINGS_PAGES: SettingsPage[] = [
  {
    path: '/fields',
    label: 'Fields',
    description:
      'Named attributes on transactions — enum picks or freeform text',
    icon: <Layers />,
  },
  {
    path: '/units',
    label: 'Units',
    description:
      'Custom currencies for non-monetary wallets — passes, miles, credits',
    icon: <Coins />,
  },
  {
    path: '/payee-logos',
    label: 'Payee logos',
    description:
      'Brand logos assigned per payee, shown on every matching transaction',
    icon: <ImageIcon />,
  },
  {
    path: '/defaults',
    label: 'Defaults',
    description:
      'Accounts and categories that recurring flows target automatically',
    icon: <SlidersHorizontal />,
  },
  {
    path: '/connections',
    label: 'Connections',
    description: 'Linked institutions, Plaid access tokens, and sync health',
    icon: <Plug />,
  },
];

/**
 * Hub for configuration pages that used to occupy their own sidebar slots.
 * The sub-pages keep their routes and remain deep-linkable; this page is
 * just the index.
 */
export const SettingsView = () => {
  useBreadcrumb({label: 'Settings', reset: true});
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4 overflow-y-auto p-4">
      <div>
        <h2 className="text-lg font-semibold">Settings</h2>
        <p className="text-muted-foreground text-sm">
          App configuration and connected services
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {SETTINGS_PAGES.map((page) => (
          <Link
            key={page.path}
            to={page.path}
            className="border-border bg-card hover:bg-accent rounded-lg border p-4 transition-colors"
          >
            <div className="text-muted-foreground flex items-center gap-2 [&>svg]:size-4 [&>svg]:shrink-0">
              {page.icon}
              <h3 className="text-foreground font-medium">{page.label}</h3>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {page.description}
            </p>
          </Link>
        ))}
      </div>

      {/* A phone has no sidebar, whose footer holds Log out on desktop. */}
      {isMobile && (
        <button
          type="button"
          onClick={logout}
          className="border-border bg-card text-destructive flex cursor-pointer items-center gap-2 rounded-lg border p-4 text-left font-medium [&>svg]:size-4"
        >
          <LogOut />
          Log out
        </button>
      )}
    </div>
  );
};
